import { useEffect, useState } from "react";

const STORAGE_KEY = "majourneys.recentBooking";
const EVENT_NAME = "majourneys:recent-booking";

interface RecentBookingMap {
  [activitySlug: string]: { reference: string; createdAt: number };
}

function readMap(): RecentBookingMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as RecentBookingMap;
  } catch {
    return {};
  }
}

function writeMap(map: RecentBookingMap) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota exceeded / disabled storage — silently no-op */
  }
}

/** Persist the most recent booking reference for an activity slug. */
export function setRecentBooking(activitySlug: string, reference: string): void {
  const map = readMap();
  map[activitySlug] = { reference, createdAt: Date.now() };
  writeMap(map);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { activitySlug, reference } }),
    );
  }
}

/** Read the most recent booking reference for an activity slug, or null. */
export function getRecentBooking(activitySlug: string): string | null {
  const map = readMap();
  return map[activitySlug]?.reference ?? null;
}

/** Remove a stored booking reference (e.g. after the user dismisses it). */
export function clearRecentBooking(activitySlug: string): void {
  const map = readMap();
  if (!map[activitySlug]) return;
  delete map[activitySlug];
  writeMap(map);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { activitySlug, reference: null } }),
    );
  }
}

/**
 * React hook: returns the most recent booking ref for `activitySlug`, kept in
 * sync with both same-tab updates (via custom event) and cross-tab updates
 * (via the `storage` event).
 */
export function useRecentBooking(activitySlug: string | undefined): string | null {
  const [reference, setReference] = useState<string | null>(() =>
    activitySlug ? getRecentBooking(activitySlug) : null,
  );

  useEffect(() => {
    if (!activitySlug) {
      setReference(null);
      return;
    }
    const slug = activitySlug;
    setReference(getRecentBooking(slug));

    function handleSameTab(e: Event) {
      const custom = e as CustomEvent<{ activitySlug: string; reference: string | null }>;
      if (custom.detail?.activitySlug !== slug) return;
      setReference(custom.detail.reference);
    }
    function handleCrossTab(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setReference(getRecentBooking(slug));
    }

    window.addEventListener(EVENT_NAME, handleSameTab);
    window.addEventListener("storage", handleCrossTab);
    return () => {
      window.removeEventListener(EVENT_NAME, handleSameTab);
      window.removeEventListener("storage", handleCrossTab);
    };
  }, [activitySlug]);

  return reference;
}
